// Typed client for the Pages Functions. All network calls go through here.

import type {
  CreateEventResponse,
  EventCategory,
  EventsResponse,
  GeocodeResponse,
  LocalEventDetail,
  LocalEventInput,
  AttachmentMeta,
} from '../types/event';

export interface EventQuery {
  keyword?: string;
  lat: number;
  lng: number;
  radiusMiles?: number;
  days?: number;
  categories?: EventCategory[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public fields?: Record<string, string>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(res: Response): Promise<never> {
  let message = `request failed (${res.status})`;
  let fields: Record<string, string> | undefined;
  try {
    const body = (await res.json()) as { error?: string; fields?: Record<string, string> };
    if (body?.error) message = body.error;
    if (body?.fields) fields = body.fields;
  } catch {
    // ignore non-JSON bodies
  }
  throw new ApiError(message, res.status, fields);
}

export async function fetchEvents(query: EventQuery): Promise<EventsResponse> {
  const url = new URL('/api/events', location.origin);
  url.searchParams.set('lat', String(query.lat));
  url.searchParams.set('lng', String(query.lng));
  if (query.keyword) url.searchParams.set('keyword', query.keyword);
  if (query.radiusMiles) url.searchParams.set('radius', String(query.radiusMiles));
  if (query.days) url.searchParams.set('days', String(query.days));
  if (query.categories && query.categories.length > 0) {
    url.searchParams.set('categories', query.categories.join(','));
  }
  // The server uses this to compute "start of today" in the user's timezone.
  url.searchParams.set('tzOffset', String(new Date().getTimezoneOffset()));

  const res = await fetch(url.toString());
  if (!res.ok) await parseError(res);
  return (await res.json()) as EventsResponse;
}

export async function geocodeZip(zip: string): Promise<GeocodeResponse> {
  const url = new URL('/api/geocode', location.origin);
  url.searchParams.set('zip', zip);
  const res = await fetch(url.toString());
  if (!res.ok) await parseError(res);
  return (await res.json()) as GeocodeResponse;
}

// --- runtime config (Turnstile site key, base url) ---

export interface AppConfig {
  turnstileSiteKey: string;
  appBaseUrl: string;
  postingEnabled: boolean;
}

let configCache: AppConfig | null = null;
export async function getConfig(): Promise<AppConfig> {
  if (configCache) return configCache;
  const res = await fetch('/api/config');
  if (!res.ok) await parseError(res);
  configCache = (await res.json()) as AppConfig;
  return configCache;
}

// --- local event CRUD ---

export async function createEvent(
  input: LocalEventInput,
  turnstileToken: string
): Promise<CreateEventResponse> {
  const res = await fetch('/api/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, turnstileToken }),
  });
  if (!res.ok) await parseError(res);
  return (await res.json()) as CreateEventResponse;
}

export async function getEventDetail(id: string, editToken?: string): Promise<LocalEventDetail> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}`, {
    headers: editToken ? { 'X-Edit-Token': editToken } : {},
  });
  if (!res.ok) await parseError(res);
  return (await res.json()) as LocalEventDetail;
}

export async function updateEvent(
  id: string,
  input: LocalEventInput,
  auth: { editToken?: string }
): Promise<LocalEventDetail> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(auth.editToken ? { 'X-Edit-Token': auth.editToken } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) await parseError(res);
  return (await res.json()) as LocalEventDetail;
}

export async function deleteEvent(
  id: string,
  auth: { editToken?: string; soft?: boolean }
): Promise<void> {
  const url = new URL(`/api/events/${encodeURIComponent(id)}`, location.origin);
  if (auth.soft) url.searchParams.set('soft', '1');
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: auth.editToken ? { 'X-Edit-Token': auth.editToken } : {},
  });
  if (!res.ok) await parseError(res);
}

// --- attachments ---

export async function uploadAttachment(
  eventId: string,
  file: File,
  editToken?: string
): Promise<AttachmentMeta> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/events/${encodeURIComponent(eventId)}/attachments`, {
    method: 'POST',
    headers: editToken ? { 'X-Edit-Token': editToken } : {},
    body: form,
  });
  if (!res.ok) await parseError(res);
  return (await res.json()) as AttachmentMeta;
}

export async function deleteAttachment(
  eventId: string,
  attachmentId: string,
  editToken?: string
): Promise<void> {
  const res = await fetch(
    `/api/events/${encodeURIComponent(eventId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE', headers: editToken ? { 'X-Edit-Token': editToken } : {} }
  );
  if (!res.ok) await parseError(res);
}

// --- admin ---

export async function adminLogin(password: string): Promise<void> {
  const res = await fetch('/api/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) await parseError(res);
}

export async function adminLogout(): Promise<void> {
  await fetch('/api/admin/logout', { method: 'POST' });
}

// Quiet login-state probe (always 200, no console 401).
export async function adminSession(): Promise<boolean> {
  try {
    const res = await fetch('/api/admin/session');
    if (!res.ok) return false;
    const body = (await res.json()) as { authenticated?: boolean };
    return Boolean(body.authenticated);
  } catch {
    return false;
  }
}

export async function adminListEvents(): Promise<{ events: LocalEventDetail[]; total: number }> {
  const res = await fetch('/api/admin/events');
  if (!res.ok) await parseError(res);
  return (await res.json()) as { events: LocalEventDetail[]; total: number };
}

// Admin-only status change (soft-remove or restore). Relies on the session cookie.
export async function adminSetStatus(
  id: string,
  status: 'published' | 'removed'
): Promise<void> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) await parseError(res);
}
